"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.shouldCalculateFetch = shouldCalculateFetch;
exports.mergeError = exports.mergeDelete = exports.mergeSuccess = exports.mergeLoading = exports.getFetch = void 0;

var _pick2 = _interopRequireDefault(require("lodash/fp/pick"));

var _Fetch = _interopRequireDefault(require("../../models/Fetch"));

var _Status = require("../../models/Status");

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

// these are `WeakMap` caches that use objects as keys
// these caches cache the calculated fetch against both the fetch from the store and the bucket from
// the store. this is NOT done to save the time calculating the new fetch, but instead done to
// return the same pointer/object reference so a `PureComponent` won't re-render

/** caches the calculated fetch against a fetch in the store */
var fetchesCache = new WeakMap();
/** caches the calculated fetch against the whole bucket in the store */

var bucketCache = new WeakMap();

function shouldCalculateFetch(fetch, bucket) {
  if (!fetch) return true;
  if (!bucket) return true;
  if (!fetchesCache.has(fetch)) return true;
  if (!bucketCache.has(bucket)) return true;
  var fromFetchCache = fetchesCache.get(fetch);
  var fromBucketCache = bucketCache.get(bucket); // if these pointers don't match then that means that either the fetch or the bucket from the
  // store changed

  if (fromFetchCache !== fromBucketCache) return true;
  return false;
}
/**
 * Here are some general Helpers which glue a few things
 * together on a reducer when using Fetch objects.
 *
 * If you have a reducer that acts like database of objects
 * then make sure you have the following props on your states
 * and actions.
 *
 * state: {
 *   objects,
 *   fetches
 * }
 *
 * actions:
 * {
 *   type: string,
 *   identifier: string,
 *   payload: {
 *     data: single or array (must have .id prop),
 *     error: string
 *   }
 * }
 *
 * Since this is Javascript we do everything loose and fast,
 * it's your fault if you attempt to use these methods and
 * your states and actions do not conform to the expected
 * forms.
 */

/**
 * Queries a reducers state to find the matching Fetch object and
 * then creates a new one based on ID and fills out the objects
 * listed in the .ids array.
 *
 * @param {string} identifier The identifier of the Fetch to look up.
 * @param {Object} state The reduced state to look at.
 *
 * @return Fetch A new fetch object
 */


var getFetch = function getFetch(identifier, state) {
  var bucketKey = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'objects';
  var bucket = state[bucketKey];
  var fetch = state.fetches[identifier];
  if (!shouldCalculateFetch(fetch, bucket)) return fetchesCache.get(fetch);
  var prevFetch = state.fetches[identifier];
  var ids = prevFetch ? prevFetch.ids : [];
  var data = prevFetch ? prevFetch.data : {};
  var objects = Object.values((0, _pick2["default"])(ids, state[bucketKey]));
  var status = prevFetch ? prevFetch.status : null;
  return new _Fetch["default"](identifier, ids, objects, data, status);
};
/**
 * Merges a new Fetch into a new State which has the LOADING flag
 * set on its `status`.
 *
 * @param {Object} state The state object to work with.
 * @param {Object} action The action object to work with.
 * @param {bool} plural Is this fetch working with more than one object.
 *
 * @return Fetch A new fetch object
 */


exports.getFetch = getFetch;

var mergeLoading = function mergeLoading(state, action) {
  var id = action.payload.id;
  var identifier = action.identifier;
  var currentFetch = state.fetches[identifier];
  var ids = action.payload.ids || (id ? [id] : currentFetch ? currentFetch.ids : []);
  var fetch = new _Fetch["default"](identifier, ids, [], currentFetch ? currentFetch.data : {}, new _Status.Status((currentFetch ? currentFetch.status.value : 0) | _Status.States.LOADING));
  return _objectSpread(_objectSpread({}, state), {}, {
    fetches: _objectSpread(_objectSpread({}, state.fetches), {}, _defineProperty({}, fetch.identifier, fetch))
  });
};
/**
 * Merges a new Fetch into a new State which has the NORMAL flag
 * set on its `status`. All objects received in the `payload.data`
 * prop will be merged into the given bucket key on the state.
 *
 * NOTE: If the payload is a single object without an `id` property then
 * the actions `identifier` property will be used to stash the object
 * onto the buckets
 *
 * @param {Object} state The state object to work with.
 * @param {Object} action The action object to work with.
 * @param {string} bucket The key of the bucket to assign the object/s to.
 *
 * @return Fetch A new fetch object
 */


exports.mergeLoading = mergeLoading;

var mergeSuccess = function mergeSuccess(state, action) {
  var _objectSpread7;

  var bucket = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'objects';
  var identifier = action.identifier; // Array

  if (action.payload.data && action.payload.data.results) {
    var _objectSpread4;

    var objectsAndIds = action.payload.data.results.reduce(function (acc, cur, index) {
      var id = cur.id || "".concat(identifier, "-").concat(index);
      acc.objects[id] = cur;
      acc.ids.push(id);
      return acc;
    }, {
      objects: {},
      ids: []
    });
    return _objectSpread(_objectSpread({}, state), {}, (_objectSpread4 = {}, _defineProperty(_objectSpread4, bucket, _objectSpread(_objectSpread({}, state[bucket]), objectsAndIds.objects)), _defineProperty(_objectSpread4, "fetches", _objectSpread(_objectSpread({}, state.fetches), {}, _defineProperty({}, identifier, new _Fetch["default"](identifier, objectsAndIds.ids, [], action.payload.data, new _Status.Status(_Status.States.NORMAL))))), _objectSpread4));
  }

  var object = action.payload.data;
  var objectId = object.id || identifier;
  return _objectSpread(_objectSpread({}, state), {}, (_objectSpread7 = {}, _defineProperty(_objectSpread7, bucket, _objectSpread(_objectSpread({}, state[bucket]), {}, _defineProperty({}, objectId, object))), _defineProperty(_objectSpread7, "fetches", _objectSpread(_objectSpread({}, state.fetches), {}, _defineProperty({}, identifier, new _Fetch["default"](identifier, [objectId], [], action.payload.data, new _Status.Status(_Status.States.NORMAL))))), _objectSpread7));
};
/**
 * Merges a new Fetch into a new State which has the NORMAL flag
 * set on its `status` AND removes objects from the cache.
 *
 * @param {Object} state The state object to work with.
 * @param {Object} action The action object to work with.
 * @param {string} bucket The key of the bucket to assign the object/s to.
 *
 * @return Fetch A new fetch object
 */


exports.mergeSuccess = mergeSuccess;

var mergeDelete = function mergeDelete(state, action) {
  var _objectSpread9;

  var bucket = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'objects';
  var identifier = action.identifier;
  var currentFetch = state.fetches[identifier];

  var objects = _objectSpread({}, state[bucket]);

  currentFetch.ids.forEach(function (id) {
    return delete objects[id];
  });
  return _objectSpread(_objectSpread({}, state), {}, (_objectSpread9 = {}, _defineProperty(_objectSpread9, bucket, objects), _defineProperty(_objectSpread9, "fetches", _objectSpread(_objectSpread({}, state.fetches), {}, _defineProperty({}, identifier, new _Fetch["default"](identifier, [], [], action.payload.data, new _Status.Status(_Status.States.NORMAL))))), _objectSpread9));
};
/**
 * Merges a new Fetch into a new State which has the ERROR flag
 * set on its `status`.
 *
 * @param {Object} state The state object to work with.
 * @param {Object} action The action object to work with.
 * @param {string} message The error message.
 *
 * @return Fetch A new fetch object
 */


exports.mergeDelete = mergeDelete;

var mergeError = function mergeError(state, action) {
  var message = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'Well shucks, there was an error.';
  var id = action.payload.id;
  var identifier = action.identifier;
  var currentFetch = state.fetches[identifier];
  var ids = action.payload.ids || (id ? [id] : currentFetch ? currentFetch.ids : []);
  var fetch = new _Fetch["default"](identifier, ids, [], action.payload.data, new _Status.Status(_Status.States.ERROR));
  return _objectSpread(_objectSpread({}, state), {}, {
    fetches: _objectSpread(_objectSpread({}, state.fetches), {}, _defineProperty({}, fetch.identifier, fetch))
  });
};

exports.mergeError = mergeError;